import type { TimestampFormat } from '@/contexts/TimestampFormatContext';

/**
 * Format-aware fixed column widths (px) for every DataTable column flagged
 * `timestamp: true` (Alerts "First/Last Seen", Audit Logs "Timestamp", …),
 * which render through `RelativeTimeWithTooltip`. The width
 * depends on the active per-user Timestamp Format (the relative form is short,
 * the absolute form is long) and, for the absolute form, on the display zone.
 *
 * The value wraps onto two lines, each truncating rather than adding a third,
 * so these budget the longer line, not the whole string, plus `px-3` cell
 * padding (24px). Measured in Chromium at the DataTable's `text-xs` (12px) in
 * the default sans font on macOS (system-ui); Arial is a few px narrower. A
 * cell renderer must not set `text-sm`: 14px text is ~17% wider and the date
 * line no longer fits the UTC width.
 * - Relative (140): the header (label + sort + filter icons) is the widest
 *   part; it keeps to one line (KIR-5639), and "First Seen" needs 130px, so
 *   140 leaves ~10px to spare. "almost 10 years ago" (113px) wraps after
 *   "years". A longer header raises its column with
 *   {@link TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER}.
 * - Absolute, UTC (150): "September 28, 2026" (117px) on the first line,
 *   "23:59:59 UTC" (81px) or "11:59:59 PM UTC" (99px) on the second.
 * - Absolute, any other zone (185): the zone name ends the second line and
 *   must stay visible; "11:59:59 PM GMT+13:45", the longest, measures 140px.
 *
 * Lives in its own module (not the `RelativeTimeWithTooltip.tsx` component file)
 * so the helper can be a function without tripping the
 * `react-refresh/only-export-components` rule.
 */
export const TIMESTAMP_COLUMN_WIDTH_RELATIVE = 140;
export const TIMESTAMP_COLUMN_WIDTH_ABSOLUTE = 150;
export const TIMESTAMP_COLUMN_WIDTH_ABSOLUTE_ZONED = 185;

/**
 * `minWidth` for a timestamp column whose header outgrows the relative width:
 * "First Detected" (83px at the header's medium `text-xs`) plus its sort and
 * filter buttons and the cell padding, with ~10px to spare. The header label
 * keeps to one line (KIR-5639), so a narrower column cuts it short rather than
 * wrapping it. The column then never drops below it, whatever the format
 * (`useDataTableColumns` takes the larger width).
 */
export const TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER = 160;

const TIMESTAMP_COLUMN_WIDTHS: Record<
  TimestampFormat,
  { utc: number; zoned: number }
> = {
  relative: {
    utc: TIMESTAMP_COLUMN_WIDTH_RELATIVE,
    zoned: TIMESTAMP_COLUMN_WIDTH_RELATIVE,
  },
  absolute: {
    utc: TIMESTAMP_COLUMN_WIDTH_ABSOLUTE,
    zoned: TIMESTAMP_COLUMN_WIDTH_ABSOLUTE_ZONED,
  },
};

/**
 * Resolve the fixed column width for the active Timestamp Format and display
 * zone. `useDataTableColumns` re-derives its columns when the width changes.
 */
export function getTimestampColumnWidth(
  format: TimestampFormat,
  timeZone: string
): number {
  const widths = TIMESTAMP_COLUMN_WIDTHS[format];
  return timeZone === 'UTC' ? widths.utc : widths.zoned;
}
